"use client"
//@ts-ignore
import { useLabels } from "@repo/ui/hooks/use-labels"
import { cn } from "@repo/ui/lib/utils"
import { BarChart3, Bell, BookOpen, Briefcase, ChevronDown, ChevronRight, ClipboardCheck, ClipboardList, List, Map, Menu, Phone, Plus, Rows2, Settings, Shield, Sprout, TreePine, UserCog, Users, Zap } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"
import { AuthService } from "../lib/api/services/auth.service"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
import { useGetCurrentUser } from "../hooks/api/users/use-current-user"

import * as React from "react"
import { useLogger } from "../lib/logger"
// Type definition for menu items
interface MenuItem {
  name: string;
  href: string;
  icon: React.ElementType;
  subMenu?: MenuItem[];
  roles?: string[]; // Array of roles that can access this item
}

// Function to create navigation items with labels
const createNavigationItems = (sidebarLabels: any) => [
  { name: sidebarLabels.navigation.dashboard, href: "/admin/dashboard", icon: BarChart3, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  { name: sidebarLabels.navigation.mapView, href: "/admin/inventaires/map", icon: Map, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  {
    name: sidebarLabels.navigation.treeInventory,
    href: "/admin/inventaires",
    icon: TreePine,
    roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.navigation.inventaireArbre, href: "/admin/inventaires?type=ARBRE", icon: TreePine, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.navigation.inventaireAlignement, href: "/admin/inventaires?type=ALIGNEMENT", icon: Rows2, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.navigation.inventaireEspaceBoise, href: "/admin/inventaires?type=EB", icon: Sprout, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ]
  },
  { name: sidebarLabels.navigation.validationPending, href: "/admin/inventaires/not-validated", icon: ClipboardList, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  {
    name: sidebarLabels.navigation.workManagement,
    href: "/travaux",
    icon: Briefcase,
    roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.workManagement.alerts, href: "/admin/inventaires/alerts", icon: Bell, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.workManagement.work, href: "/admin/inventaires/travaux", icon: ClipboardCheck, roles: ["MANAGER", "ADMIN", "DENDROMAP", "AGENT"] },
      { name: sidebarLabels.workManagement.visite, href: "/admin/inventaires/visites", icon: ClipboardList, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ]
  },

  {
    name: sidebarLabels.navigation.newPlantation,
    href: "/admin/plantations",
    icon: Plus,
    roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.newPlantation.createArbre, href: "/admin/plantations?create=arbre", icon: TreePine, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.newPlantation.createAlignement, href: "/admin/plantations?create=alignement", icon: Rows2, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.newPlantation.createEspaceBoise, href: "/admin/plantations?create=epaysage", icon: Sprout, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ],
  },

  {
    name: sidebarLabels.navigation.library,
    href: "/admin/bibliotheque",
    icon: BookOpen,
    roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.library.essences, href: "/admin/bibliotheque/essences", icon: TreePine, roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.library.mushrooms, href: "/admin/bibliotheque/champignons", icon: Sprout, roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ],
  },
]

const createConfigurationItems = (sidebarLabels: any) => [
  { name: sidebarLabels.configuration.promoCode, href: "/admin/promo-codes", icon: Settings, roles: ["ADMIN"] },
  { name: sidebarLabels.configuration.contactManagement, href: "/admin/contacts", icon: Phone, roles: ["ADMIN"] },
]

const createAdministrationItems = (sidebarLabels: any) => [
  {
    name: sidebarLabels.administration.rightsManagement,
    href: "/admin/droits",
    icon: Shield,
    roles: ["ADMIN", "DENDROMAP", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.rightsManagement.group, href: "/admin/droits/groupe", icon: Users, roles: ["ADMIN"] },
      { name: sidebarLabels.rightsManagement.profile, href: "/admin/droits/profil", icon: UserCog, roles: ["ADMIN"] },
      { name: sidebarLabels.rightsManagement.user, href: "/admin/droits/utilisateur", icon: Users, roles: ["ADMIN", "DENDROMAP", "MANAGER"] },
      { name: sidebarLabels.rightsManagement.quickCreate, href: "/admin/droits/creation-rapide", icon: Zap, roles: ["ADMIN"] },
    ]
  },
  {
    name: sidebarLabels.administration.savedLists,
    href: "/listes",
    icon: List,
    roles: ["ADMIN"],
    subMenu: [
      { name: sidebarLabels.savedLists.essences, href: "/admin/config-inventaire/essences", icon: TreePine, roles: ["ADMIN"] },
      { name: sidebarLabels.savedLists.mushrooms, href: "/admin/config-inventaire/champignons", icon: Sprout, roles: ["ADMIN"] },
    ]
  },
]

// Shared active/inactive link class builders
const navLinkClass = (isActive: boolean, isCollapsed: boolean) =>
  cn(
    "flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-all duration-200 rounded-md min-w-0",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d2e1a]",
    isActive
      ? "bg-white/20 text-white font-semibold border-l-[3px] border-emerald-400/80 shadow-sm"
      : "text-white/70 hover:bg-white/10 hover:text-white",
    isCollapsed && "justify-center px-2"
  )

const navButtonClass = (isActive: boolean, isCollapsed: boolean) =>
  cn(
    "flex w-full items-center justify-between px-3 py-2 text-[13px] font-medium transition-all duration-200 rounded-md min-w-0",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d2e1a]",
    isActive
      ? "bg-white/20 text-white font-semibold border-l-[3px] border-emerald-400/80 shadow-sm"
      : "text-white/70 hover:bg-white/10 hover:text-white",
    isCollapsed && "justify-center px-2"
  )

const subLinkClass = (isActive: boolean) =>
  cn(
    "flex items-center gap-2.5 px-3 py-1.5 text-[12px] font-medium transition-all duration-200 rounded-md min-w-0",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d2e1a]",
    isActive
      ? "bg-white/20 text-white"
      : "text-white/60 hover:bg-white/10 hover:text-white"
  )

export function Sidebar() {
  const { pathname, search } = useLocation();
  const logger = useLogger("Sidebar");
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const [userRole, setUserRole] = useState<string | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const { labels } = useLabels()
  const sidebarLabels = labels.sidebar
  const { data: currentUser } = useGetCurrentUser()

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    // Get the user's role
    const role = AuthService.getRole()
    logger.info("role ", role)
    setUserRole(role)
  }, [isMounted])

  // Listen for toggle events from the header
  useEffect(() => {
    if (!isMounted) return;

    const handleToggle = () => {
      setIsCollapsed(prev => !prev);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('toggle-sidebar', handleToggle);
    }
    return () => window.removeEventListener('toggle-sidebar', handleToggle);
  }, [isMounted]);

  // Update CSS custom property for sidebar width
  useEffect(() => {
    if (!isMounted || typeof document === 'undefined') return;

    const sidebarWidth = isCollapsed ? '4rem' : '15rem'
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth)

    return () => {
      if (typeof document !== 'undefined') {
        document.documentElement.style.removeProperty('--sidebar-width')
      }
    }
  }, [isCollapsed, isMounted])

  const toggleMenu = (menuName: string) => {
    if (expandedMenus.includes(menuName)) {
      setExpandedMenus(expandedMenus.filter(item => item !== menuName))
    } else {
      setExpandedMenus([...expandedMenus, menuName])
    }
  }

  // Filter menu items based on user role
  const filterMenuItems = (items: MenuItem[]) => {
    if (!userRole) return items // If role is not loaded yet, show all items

    return items.filter(item => {
      // If no roles are specified, item is accessible to all
      if (!item.roles || item.roles.length === 0) return true

      // Check if the user's role is included in the item's roles
      return item.roles.includes(userRole)
    }).map(item => {
      // Also filter submenu items
      if (item.subMenu) {
        return {
          ...item,
          subMenu: item.subMenu.filter(subItem => {
            if (!subItem.roles || subItem.roles.length === 0) return true
            return subItem.roles.includes(userRole)
          })
        }
      }
      return item
    })
  }

  const navigation = createNavigationItems(sidebarLabels)
  const configuration = createConfigurationItems(sidebarLabels)
  const administration = createAdministrationItems(sidebarLabels)

  const filteredNavigation = filterMenuItems(navigation)
  const filteredConfiguration = filterMenuItems(configuration)
  const filteredAdministration = filterMenuItems(administration)

  const userPrenom = (currentUser as any)?.data?.prenom ?? ''
  const userNom = (currentUser as any)?.data?.nom ?? ''
  const userInitials = `${userPrenom.charAt(0)}${userNom.charAt(0)}`.toUpperCase() || '??'
  const fullName = [userPrenom, userNom].filter(Boolean).join(' ')

  return (
    <TooltipProvider>
      <div className={cn(
        "flex h-screen flex-col transition-all border-r border-white/10 sidebar-gradient",
        isCollapsed ? "w-16" : "w-60",
        "shadow-lg"
      )}>
        {/* Logo */}
        <div className="flex items-center justify-center p-3 border-b border-white/10 shadow-sm">
          <Link
            to="/home"
            aria-label="Go to home"
            className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d2e1a] hover:bg-white/5 transition-colors"
          >
            {!isCollapsed && (
              <div className="flex flex-col items-center justify-center gap-1.5 px-2 py-1.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 p-2">
                  <img src="/images/logos/dark.svg" alt="Dendromap" className="w-full h-full object-contain drop-shadow-md" />
                </div>
                <div className="text-center">
                  <h1 className="text-base font-semibold text-white drop-shadow-sm leading-tight">{sidebarLabels.brand}</h1>
                  <p className="text-[11px] text-white/50 leading-snug">{sidebarLabels.tagline}</p>
                </div>
              </div>
            )}

            {isCollapsed && (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg p-2">
                <img src="/images/logos/dark.svg" alt="Dendromap" className="w-full h-full object-contain drop-shadow-md" />
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-0.5 p-2.5 pr-2 overflow-y-auto scroll-smooth overscroll-contain dendro-scrollbar">
          {filteredNavigation.map((item) => {
            const isActive = pathname === item.href;
            const isExpanded = expandedMenus.includes(item.name) || (item.subMenu && pathname.startsWith(item.href))

            return (
              <div key={item.name}>
                {item.subMenu && item.subMenu.length > 0 ? (
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={navButtonClass(isActive, isCollapsed)}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={cn("flex items-center gap-3 min-w-0 flex-1", isCollapsed && "justify-center w-full")}>
                          <item.icon className="h-[18px] w-[18px] text-white shrink-0" />
                          {!isCollapsed && <span className="truncate">{item.name}</span>}
                        </div>
                      </TooltipTrigger>
                      {isCollapsed && <TooltipContent side="right">{item.name}</TooltipContent>}
                    </Tooltip>

                    {!isCollapsed && (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-white/60 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-white/60 shrink-0" />
                      )
                    )}
                  </button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.href}
                        className={navLinkClass(isActive, isCollapsed)}
                      >
                        <item.icon className={cn(
                          "h-[18px] w-[18px] shrink-0",
                          isActive ? "text-emerald-300" : "text-white/60 group-hover:text-white"
                        )} />
                        {!isCollapsed && <span className="truncate">{item.name}</span>}
                      </Link>
                    </TooltipTrigger>
                    {isCollapsed && <TooltipContent side="right">{item.name}</TooltipContent>}
                  </Tooltip>
                )}

                {/* Submenu */}
                {item.subMenu && item.subMenu.length > 0 && isExpanded && !isCollapsed && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-2.5">
                    {item.subMenu.map((subItem) => {
                      const isSubActive = (pathname + search) === subItem.href || pathname === subItem.href
                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={subLinkClass(isSubActive)}
                        >
                          <subItem.icon className={cn(
                            "h-[14px] w-[14px] shrink-0",
                            isSubActive ? "text-emerald-300" : "text-white/50"
                          )} />
                          <span className="truncate">{subItem.name}</span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {filteredConfiguration.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              {!isCollapsed && (
                <h3 className="px-3 text-[10px] font-semibold uppercase tracking-widest mb-2 text-white/40">
                  {sidebarLabels.configuration.title}
                </h3>
              )}
              <div className="space-y-0.5">
                {filteredConfiguration.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Tooltip key={item.name}>
                      <TooltipTrigger asChild>
                        <Link
                          to={item.href}
                          className={navLinkClass(isActive, isCollapsed)}
                        >
                          <item.icon className={cn(
                            "h-[18px] w-[18px] shrink-0",
                            isActive ? "text-emerald-300" : "text-white/60"
                          )} />
                          {!isCollapsed && <span className="truncate">{item.name}</span>}
                        </Link>
                      </TooltipTrigger>
                      {isCollapsed && <TooltipContent side="right">{item.name}</TooltipContent>}
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}

          {filteredAdministration.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              {!isCollapsed && (
                <h3 className="px-3 text-[10px] font-semibold uppercase tracking-widest mb-2 text-white/40">
                  {sidebarLabels.administration.title}
                </h3>
              )}
              <div className="space-y-0.5">
                {filteredAdministration.map((item) => {
                  const isActive = pathname === item.href || pathname.startsWith(item.href + '/')
                  const isExpanded = expandedMenus.includes(item.name) || (item.subMenu && pathname.startsWith(item.href))

                  return (
                    <div key={item.name}>
                      {item.subMenu && item.subMenu.length > 0 ? (
                        <button
                          onClick={() => toggleMenu(item.name)}
                          className={navButtonClass(isActive, isCollapsed)}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={cn("flex items-center gap-3 min-w-0 flex-1", isCollapsed && "justify-center w-full")}>
                                <item.icon className={cn(
                                  "h-[18px] w-[18px] shrink-0",
                                  isActive ? "text-emerald-300" : "text-white/60"
                                )} />
                                {!isCollapsed && <span className="truncate">{item.name}</span>}
                              </div>
                            </TooltipTrigger>
                            {isCollapsed && <TooltipContent side="right">{item.name}</TooltipContent>}
                          </Tooltip>

                          {!isCollapsed && (
                            isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-white/60 shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-white/60 shrink-0" />
                            )
                          )}
                        </button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={item.href}
                              className={navLinkClass(isActive, isCollapsed)}
                            >
                              <item.icon className={cn(
                                "h-[18px] w-[18px] shrink-0",
                                isActive ? "text-emerald-300" : "text-white/60"
                              )} />
                              {!isCollapsed && <span className="truncate">{item.name}</span>}
                            </Link>
                          </TooltipTrigger>
                          {isCollapsed && <TooltipContent side="right">{item.name}</TooltipContent>}
                        </Tooltip>
                      )}

                      {/* Submenu */}
                      {item.subMenu && item.subMenu.length > 0 && isExpanded && !isCollapsed && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-2.5">
                          {item.subMenu.map((subItem) => {
                            const isSubActive = pathname === subItem.href
                            return (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className={subLinkClass(isSubActive)}
                              >
                                <subItem.icon className={cn(
                                  "h-[14px] w-[14px] shrink-0",
                                  isSubActive ? "text-emerald-300" : "text-white/50"
                                )} />
                                <span className="truncate">{subItem.name}</span>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </nav>

        {/* Bottom user row */}
        <div className="border-t border-white/10 p-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/admin/account"
                className={cn(
                  "flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/10",
                  isCollapsed && "justify-center"
                )}
              >
                {/* Avatar */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600/80 ring-2 ring-emerald-400/30 text-white text-xs font-semibold">
                  {userInitials}
                </div>

                {!isCollapsed && (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-medium text-white leading-tight">
                      {fullName || '—'}
                    </p>
                    <p className="truncate text-[10px] text-white/40 leading-tight uppercase tracking-wider">
                      {userRole ?? ''}
                    </p>
                  </div>
                )}
              </Link>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right">
                <span className="font-medium">{fullName}</span>
                {userRole && <span className="ml-1 text-muted-foreground">· {userRole}</span>}
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  )
}
